"use client";

// RingControls — the private ring's per-app pieces since 149, shared by the
// access sheet, the you page and (the answer row) the inbox. Each is the
// settings family's own controls and nothing louder: quiet links for answers,
// the segmented track for one-of-a-few, chips for a choice of many.
//
//   AllowChoices  answer an app's request: for 30 minutes, a day, a week,
//                 always, or no. The usual length is lit: a day when what it
//                 asked about is sensitive (or unknown), a week otherwise.
//                 Never always by default. The grant covers what it asked about.
//   GrantList     the grants an app holds now, each with "end it".
//   GrantMore     let an app read more from settings: how deep, about what,
//                 for how long, said back in one line before it is given.
//   WriteControl  what an app may write: writes freely · asks first · read only.
//   HeldWrites    what an app that asks first left for a yes: keep or let go.
//   Receipts      what it read, and when: names and counts, never a word.

import { useId, useState } from "react";
import { DEPTH_WORDS, LENGTH_SAID, LENGTH_WORDS, WRITE_WORDS, agoWords, aboutWords, grantLine, proposalHeadline, receiptLine } from "../../lib/self/ringView";
import type { ProposalView, ReceiptView, RingApp, RingRequest, RingWindow } from "../../lib/self/ringView";
import { GRANT_LENGTHS, WRITE_MODES, defaultLength, type GrantLength, type WriteMode } from "../../lib/self/ring";
import { CATEGORY_DEFS, categoryWords, type Category } from "../../lib/self/categories";
import type { Decision } from "./useRing";

export type Decide = (decision: Decision, how?: { length?: GrantLength; categories?: Category[] | null }) => void;

/** Answer one request. Before 149 it is 128's three answers. */
export function AllowChoices({
  req,
  passes,
  className = "quiet-link",
  onDecide,
}: {
  req: Pick<RingRequest, "categories">;
  passes: boolean;
  className?: string;
  onDecide: Decide;
}) {
  if (!passes) {
    return (
      <>
        <button type="button" className={className} onClick={() => onDecide("allowed_once")} title="for the next 30 minutes">
          allow once
        </button>
        <button type="button" className={className} onClick={() => onDecide("allowed_always")}>
          always
        </button>
        <button type="button" className={className} onClick={() => onDecide("denied")}>
          no
        </button>
      </>
    );
  }
  const usual = defaultLength(req.categories);
  const categories = req.categories?.length ? req.categories : null;
  return (
    <>
      <span className="prv-allow-for">allow for</span>
      {GRANT_LENGTHS.map((l) => (
        <button
          key={l}
          type="button"
          className={className}
          data-usual={l === usual || undefined}
          onClick={() => onDecide(l === "always" ? "allowed_always" : "allowed_once", { length: l, categories })}
        >
          {LENGTH_WORDS[l]}
        </button>
      ))}
      <button type="button" className={className} onClick={() => onDecide("denied")}>
        no
      </button>
    </>
  );
}

/** The grants one app holds now. */
export function GrantList({ windows, now, onEnd }: { windows: RingWindow[]; now: number; onEnd: (requestId: string) => void }) {
  if (!windows.length) return null;
  return (
    <ul className="acs-seen rc-grants">
      {windows.map((w) => (
        <li key={w.requestId} className="rc-line">
          <span className="prv-fact">{grantLine(w, now)}</span>
          <span className="prv-meta">
            {w.from === "settings" ? "you gave this in settings" : "you allowed it when it asked"}
            {" · "}
            <button type="button" className="quiet-link" onClick={() => onEnd(w.requestId)}>
              end it
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Let one app read more: how deep, about what, for how long. */
export function GrantMore({
  app,
  onGrant,
}: {
  app: Pick<RingApp, "kind" | "id" | "label">;
  onGrant: (depth: "personal" | "private", length: GrantLength, categories: Category[] | null) => Promise<boolean>;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [depth, setDepth] = useState<"personal" | "private">("personal");
  const [cats, setCats] = useState<Category[]>([]);
  const [length, setLength] = useState<GrantLength | null>(null);
  const [busy, setBusy] = useState(false);
  const chosen = cats.length ? cats : null;
  const len = length ?? defaultLength(chosen);

  if (!open) {
    return (
      <p className="prv-note">
        <button type="button" className="quiet-link" onClick={() => setOpen(true)}>
          let it read more for a while
        </button>
      </p>
    );
  }
  const toggle = (c: Category) => setCats((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));
  const depths = ["personal", "private"] as const;
  const about = aboutWords(chosen);
  return (
    <div className="rc-more" role="group" aria-labelledby={`${id}-h`}>
      <p id={`${id}-h`} className="rc-label rc-label--head">
        let {app.label} read more
      </p>
      <div className="rc-field">
        <span className="rc-label" id={`${id}-d`}>
          how deep
        </span>
        <div className="cs-seg" role="radiogroup" aria-labelledby={`${id}-d`} style={{ "--n": 2, "--at": depths.indexOf(depth) } as React.CSSProperties}>
          <span className="cs-seg-lift" aria-hidden="true" />
          {depths.map((d) => (
            <button key={d} type="button" role="radio" aria-checked={d === depth} data-noswipe onClick={() => setDepth(d)}>
              {DEPTH_WORDS[d].label}
            </button>
          ))}
        </div>
      </div>
      <div className="rc-field">
        <span className="rc-label" id={`${id}-c`}>
          about
        </span>
        <div className="rc-chips" role="group" aria-labelledby={`${id}-c`}>
          <button type="button" className="chip chip--tiny" data-on={cats.length === 0 || undefined} aria-pressed={cats.length === 0} onClick={() => setCats([])}>
            everything
          </button>
          {CATEGORY_DEFS.map((c) => (
            <button
              key={c.key}
              type="button"
              className="chip chip--tiny"
              data-on={cats.includes(c.key) || undefined}
              aria-pressed={cats.includes(c.key)}
              onClick={() => toggle(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="rc-field">
        <span className="rc-label" id={`${id}-l`}>
          for how long
        </span>
        <div className="cs-seg" role="radiogroup" aria-labelledby={`${id}-l`} style={{ "--n": GRANT_LENGTHS.length, "--at": GRANT_LENGTHS.indexOf(len) } as React.CSSProperties}>
          <span className="cs-seg-lift" aria-hidden="true" />
          {GRANT_LENGTHS.map((l) => (
            <button key={l} type="button" role="radio" aria-checked={l === len} data-noswipe onClick={() => setLength(l)}>
              {LENGTH_WORDS[l]}
            </button>
          ))}
        </div>
      </div>
      <p className="prv-note rc-says">
        {app.label} can read your {depth === "private" ? "private and personal" : "personal"} things{about ? ` ${about}` : ""} {LENGTH_SAID[len]}. you can end it here
        any time.
      </p>
      <p className="prv-acts">
        <button
          type="button"
          className="quiet-link"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            const ok = await onGrant(depth, len, chosen);
            setBusy(false);
            if (ok) {
              setOpen(false);
              setCats([]);
              setLength(null);
            }
          }}
        >
          {busy ? "allowing…" : "allow it"}
        </button>
        <button type="button" className="quiet-link" onClick={() => setOpen(false)}>
          not now
        </button>
      </p>
    </div>
  );
}

/** What one app may write. */
export function WriteControl({ app, labelledBy, onSet }: { app: RingApp; labelledBy: string; onSet: (mode: WriteMode) => void }) {
  return (
    <div className="prv-ctl rc-write">
      <div className="cs-seg" role="radiogroup" aria-labelledby={labelledBy} style={{ "--n": WRITE_MODES.length, "--at": WRITE_MODES.indexOf(app.writeMode) } as React.CSSProperties}>
        <span className="cs-seg-lift" aria-hidden="true" />
        {WRITE_MODES.map((m) => (
          <button key={m} type="button" role="radio" aria-checked={m === app.writeMode} data-noswipe onClick={() => m !== app.writeMode && onSet(m)}>
            {WRITE_WORDS[m].label}
          </button>
        ))}
      </div>
      <span className="prv-meta">{WRITE_WORDS[app.writeMode].says}.</span>
    </div>
  );
}

/** What an app that asks first left for a yes, and what became of this week's. */
export function HeldWrites({ proposals, now, onAnswer }: { proposals: ProposalView[]; now: number; onAnswer: (id: string, d: "keep" | "drop") => void }) {
  if (!proposals.length) return null;
  return (
    <ul className="acs-seen rc-held">
      {proposals.map((p) => (
        <li key={p.id} className="rc-line" data-status={p.status}>
          <span className="prv-fact">{proposalHeadline(p)}</span>
          {p.status === "pending" ? (
            <>
              {p.preview?.text ? <span className="prv-ask rc-quote">&ldquo;{p.preview.text}&rdquo;</span> : null}
              {p.preview?.said ? <span className="prv-meta">in your words: &ldquo;{p.preview.said}&rdquo;</span> : null}
              {p.preview?.pages.length ? <span className="prv-meta">pages: {p.preview.pages.map((x) => x.replace(/_/g, " ")).join(", ")}</span> : null}
              {!p.preview ? <span className="prv-meta">its words would not open here.</span> : null}
              <span className="prv-meta">
                {agoWords(p.createdAt, now)}
                {p.outcome ? ` · last try: ${p.outcome}` : ""}
              </span>
              <span className="prv-acts">
                <button type="button" className="quiet-link" onClick={() => onAnswer(p.id, "keep")}>
                  keep it
                </button>
                <button type="button" className="quiet-link" onClick={() => onAnswer(p.id, "drop")}>
                  let it go
                </button>
              </span>
            </>
          ) : (
            <span className="prv-meta">
              {p.outcome ?? (p.status === "expired" ? "no answer in 30 days, so nothing was written." : "")}
              {p.decidedAt ? ` · ${agoWords(p.decidedAt, now)}` : ""}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

const RECEIPTS_SHOWN = 6;

/** What it read, and when. */
export function Receipts({ receipts, now, label }: { receipts: ReceiptView[]; now: number; label: string }) {
  const [all, setAll] = useState(false);
  if (!receipts.length) return <p className="prv-note">{label} has read nothing since read receipts began.</p>;
  const shown = all ? receipts : receipts.slice(0, RECEIPTS_SHOWN);
  return (
    <>
      <ul className="acs-seen rc-receipts">
        {shown.map((r, i) => (
          <li key={`${r.at}-${i}`}>
            <span className="prv-fact">{receiptLine(r)}</span>
            <span className="prv-meta">
              {agoWords(r.at, now)} · {r.tool.replace(/^innernet_/, "").replace(/_/g, " ")}
              {r.pages.length ? ` · ${r.pages.slice(0, 6).map((p) => p.replace(/_/g, " ")).join(", ")}${r.pages.length > 6 ? "…" : ""}` : ""}
              {r.categories.length && !r.factCount ? ` · ${categoryWords(r.categories)}` : ""}
              {r.chars ? ` · ${r.chars.toLocaleString("en-US")} characters` : ""}
            </span>
          </li>
        ))}
      </ul>
      {receipts.length > RECEIPTS_SHOWN ? (
        <p className="prv-note">
          <button type="button" className="quiet-link" onClick={() => setAll((v) => !v)}>
            {all ? "fewer" : `all ${receipts.length}`}
          </button>
        </p>
      ) : null}
    </>
  );
}
